import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  Divider,
  TextField,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Restore as RestoreIcon,
  History as HistoryIcon,
  AddCircleOutline as SnapshotIcon,
  ExpandMore as ExpandMoreIcon,
  AccountCircle as UserIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 360,
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    zIndex: 25,
    boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
    transition: "transform 0.3s ease",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    minHeight: 48,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: 3,
    },
  },
  versionItem: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(154,0,237,0.03)",
    },
  },
  versionItemActive: {
    borderColor: "#22c55e",
    backgroundColor: "rgba(34,197,94,0.05)",
  },
  versionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  versionLabel: {
    fontSize: "0.8rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  versionTime: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  versionMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: 4,
  },
  metaChip: {
    height: 20,
    fontSize: "0.65rem",
    borderRadius: 4,
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  restoreBtn: {
    fontSize: "0.7rem",
    padding: "2px 8px",
    minWidth: 0,
    textTransform: "none",
    borderRadius: 6,
  },
  loadMoreBtn: {
    width: "100%",
    textTransform: "none",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  snapshotSection: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  snapshotRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  snapshotInput: {
    flex: 1,
    "& .MuiInputBase-input": {
      fontSize: "0.8rem",
      padding: "6px 10px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
    },
  },
}));

function formatTimeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Agora";
  if (diffMin < 60) return `${diffMin}min atras`;
  if (diffH < 24) return `${diffH}h atras`;
  if (diffD < 7) return `${diffD}d atras`;

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFullDate(dateStr) {
  return new Date(dateStr).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function VersionHistory({
  open,
  onClose,
  flowId,
  onRestore,
}) {
  const classes = useStyles();
  const [versions, setVersions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [snapshotDesc, setSnapshotDesc] = useState("");
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const loadedRef = useRef(false);

  // Carregar versoes
  const loadVersions = useCallback(async (offset = 0, append = false) => {
    if (!flowId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/flowbuilder/${flowId}/versions`, {
        params: { limit: 30, offset },
      });
      if (append) {
        setVersions((prev) => [...prev, ...data.versions]);
      } else {
        setVersions(data.versions);
      }
      setTotal(data.total);
    } catch (err) {
      console.error("[VersionHistory] Erro ao carregar:", err);
      toast.error("Erro ao carregar historico de versoes");
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  // Carregar ao abrir
  useEffect(() => {
    if (open && flowId) {
      loadVersions(0, false);
      loadedRef.current = true;
    }
    if (!open) {
      loadedRef.current = false;
    }
  }, [open, flowId, loadVersions]);

  // Restaurar versao
  const handleRestore = useCallback(async (versionId, versionNum) => {
    if (!window.confirm(
      `Restaurar para a versao ${versionNum}?\n\nO estado atual sera salvo como snapshot antes da restauracao.`
    )) return;

    setRestoring(versionId);
    try {
      const { data } = await api.post(
        `/flowbuilder/${flowId}/versions/${versionId}/restore`
      );
      if (data.success && data.data) {
        onRestore(data.data);
        toast.success(`Restaurado para versao ${data.restoredVersion}`);
        // Recarregar lista
        loadVersions(0, false);
      }
    } catch (err) {
      console.error("[VersionHistory] Erro ao restaurar:", err);
      toast.error("Erro ao restaurar versao");
    } finally {
      setRestoring(null);
    }
  }, [flowId, onRestore, loadVersions]);

  // Criar snapshot manual
  const handleCreateSnapshot = useCallback(async () => {
    if (!flowId) return;
    setCreatingSnapshot(true);
    try {
      await api.post(`/flowbuilder/${flowId}/versions`, {
        description: snapshotDesc.trim() || "Snapshot manual",
      });
      setSnapshotDesc("");
      toast.success("Snapshot criado");
      loadVersions(0, false);
    } catch (err) {
      console.error("[VersionHistory] Erro ao criar snapshot:", err);
      toast.error("Erro ao criar snapshot");
    } finally {
      setCreatingSnapshot(false);
    }
  }, [flowId, snapshotDesc, loadVersions]);

  // Carregar mais
  const handleLoadMore = useCallback(() => {
    loadVersions(versions.length, true);
  }, [versions.length, loadVersions]);

  if (!open) return null;

  const hasMore = versions.length < total;

  return (
    <div className={classes.root}>
      {/* Cabecalho */}
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <HistoryIcon style={{ fontSize: 20, color: "#6366f1" }} />
          <span>Historico de Versoes</span>
          {total > 0 && (
            <Chip
              label={total}
              size="small"
              style={{
                height: 20,
                fontSize: "0.65rem",
                backgroundColor: "#ede9fe",
                color: "#6366f1",
              }}
            />
          )}
        </div>
        <div className={classes.headerActions}>
          <Tooltip title="Fechar">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Lista de versoes */}
      <div className={classes.body}>
        {loading && versions.length === 0 ? (
          <div className={classes.emptyState}>
            <CircularProgress size={28} />
            <Typography variant="body2">Carregando versoes...</Typography>
          </div>
        ) : versions.length === 0 ? (
          <div className={classes.emptyState}>
            <HistoryIcon style={{ fontSize: 40, opacity: 0.3 }} />
            <Typography variant="body2">Nenhuma versao salva ainda</Typography>
            <Typography variant="caption" style={{ textAlign: "center", maxWidth: 240 }}>
              As versoes sao criadas automaticamente a cada salvamento do fluxo.
            </Typography>
          </div>
        ) : (
          <>
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`${classes.versionItem} ${idx === 0 ? classes.versionItemActive : ""}`}
              >
                <div className={classes.versionHeader}>
                  <span className={classes.versionLabel}>
                    v{v.version}
                    {idx === 0 && (
                      <span style={{ color: "#22c55e", fontWeight: 400, marginLeft: 6, fontSize: "0.7rem" }}>
                        (atual)
                      </span>
                    )}
                  </span>
                  <Tooltip title={formatFullDate(v.createdAt)}>
                    <span className={classes.versionTime}>
                      {formatTimeAgo(v.createdAt)}
                    </span>
                  </Tooltip>
                </div>

                {/* Descricao */}
                {v.description && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#666",
                      display: "block",
                      marginBottom: 4,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.description}
                  </Typography>
                )}

                {/* Meta info */}
                <div className={classes.versionMeta}>
                  <Chip
                    className={classes.metaChip}
                    label={`${v.nodeCount} nodes`}
                    size="small"
                    variant="outlined"
                    style={{ borderColor: "#e0e0e0" }}
                  />
                  <Chip
                    className={classes.metaChip}
                    label={`${v.connectionCount} conexoes`}
                    size="small"
                    variant="outlined"
                    style={{ borderColor: "#e0e0e0" }}
                  />

                  {/* Botao restaurar (nao mostra na versao atual) */}
                  {idx > 0 && (
                    <Tooltip title="Restaurar esta versao">
                      <Button
                        className={classes.restoreBtn}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={
                          restoring === v.id ? (
                            <CircularProgress size={12} />
                          ) : (
                            <RestoreIcon style={{ fontSize: 14 }} />
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestore(v.id, v.version);
                        }}
                        disabled={restoring !== null}
                        style={{ marginLeft: "auto" }}
                      >
                        Restaurar
                      </Button>
                    </Tooltip>
                  )}
                </div>

                {/* Usuario */}
                <div className={classes.userInfo}>
                  <UserIcon style={{ fontSize: 14 }} />
                  <span>{v.userName}</span>
                </div>
              </div>
            ))}

            {/* Carregar mais */}
            {hasMore && (
              <Button
                className={classes.loadMoreBtn}
                variant="outlined"
                size="small"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} /> : <ExpandMoreIcon />}
              >
                {loading ? "Carregando..." : `Carregar mais (${total - versions.length} restantes)`}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Secao de snapshot manual */}
      <div className={classes.snapshotSection}>
        <div className={classes.snapshotRow}>
          <TextField
            className={classes.snapshotInput}
            variant="outlined"
            size="small"
            placeholder="Descricao do snapshot..."
            value={snapshotDesc}
            onChange={(e) => setSnapshotDesc(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateSnapshot();
            }}
          />
          <Tooltip title="Criar snapshot manual">
            <IconButton
              size="small"
              color="primary"
              onClick={handleCreateSnapshot}
              disabled={creatingSnapshot}
            >
              {creatingSnapshot ? (
                <CircularProgress size={18} />
              ) : (
                <SnapshotIcon style={{ fontSize: 22 }} />
              )}
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
